import { math } from '../math';

export interface UnitMeta{
	abbreviation:string;
	abbreviationAscii:string;
	fullName:string;
	decimalCount:number;
	fromBase:(x:number)=>number;
	toBase:(x:number)=>number;
}

interface UnitMetaInput{
	abbreviation:string;
	abbreviationAscii?:string;
	fullName?:string;
	decimalCount:number;
	fromBase:(x:number)=>number;
	toBase:(x:number)=>number;
}

function typeUnitMeta<T extends {[unit:string]:UnitMetaInput}>(table:T){
	return <{[key in keyof T]:UnitMeta}>Object.fromEntries(Object.entries(table).map(([k,v])=>{
		let abbreviationAscii=v.abbreviationAscii ?? v.abbreviation;
		if(abbreviationAscii==='°')
			abbreviationAscii='deg';
		const meta:UnitMeta={
			...v,
			fullName: v.fullName ?? k,
			abbreviationAscii,
		};
		return [<keyof T>k,meta];
	}));
}

export const unitMeta=typeUnitMeta({
	none: {
		abbreviation: "",
		fullName: "",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},

	//---distance---
	millimeter: {
		abbreviation: "mm",
		decimalCount: 0,
		fromBase: (x)=>x/0.001,
		toBase: (x)=>x*0.001,
	},
	centimeter: {
		abbreviation: "cm",
		decimalCount: 2,
		fromBase: (x)=>x/0.01,
		toBase: (x)=>x*0.01,
	},
	inch: {
		abbreviation: "in",
		decimalCount: 4,
		fromBase: (x)=>x*12/0.3048,
		toBase: (x)=>x*0.3048/12,
	},
	decameter: {
		abbreviation: "dm",
		decimalCount: 2,
		fromBase: (x)=>x/0.1,
		toBase: (x)=>x*0.1,
	},
	feet: {
		abbreviation: "ft",
		fullName: "feet",
		decimalCount: 1,
		fromBase: (x)=>x/0.3048,
		toBase: (x)=>x*0.3048,
	},
	usfeet: {
		abbreviation: "us-ft",
		fullName: "us feet",
		decimalCount: 1,
		fromBase: (x)=>x*3937/1200,
		toBase: (x)=>x*1200/3937,
	},
	yard: {
		abbreviation: "yd",
		fullName: "yard",
		decimalCount: 2,
		fromBase: (x)=>x/(0.3048*3),
		toBase: (x)=>x*0.3048*3,
	},
	meter: {
		abbreviation: "m",
		fullName: "meter",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	kilometer: {
		abbreviation: "km",
		decimalCount: 2,
		fromBase: (x)=>x/1000,
		toBase: (x)=>x*1000,
	},
	mile: {
		// offsetFromBase: 0,
		abbreviation: "mi",
		decimalCount: 2,
		fromBase: (x)=>x/1609.34,
		toBase: (x)=>x*1609.34,
	},

	//---area---
	feet2: {
		abbreviation: "ft²",
		fullName: "sqaure feet",
		decimalCount: 2,
		fromBase: (x)=>x/(0.3048**2),
		toBase: (x)=>x*(0.3048**2),
	},
	yard2: {
		abbreviation: "yd²",
		fullName: "square yard",
		decimalCount: 2,
		fromBase: (x)=>x/((0.3048*3)**2),
		toBase: (x)=>x*(0.3048*3)**2,
	},
	meter2: {
		abbreviation: "m²",
		fullName: "square meter",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	acre: {
		abbreviation: "acre",
		fullName: "acre",
		decimalCount: 2,
		fromBase: (x)=>x/4046.86,
		toBase: (x)=>x*4046.86,
	},
	kilometer2: {
		abbreviation: "km²",
		fullName: "sqaure kilometer",
		decimalCount: 2,
		fromBase: (x)=>x/1000000,
		toBase: (x)=>x*1000000,
	},

	//---volume---
	feet3: {
		abbreviation: "ft³",
		fullName: "cubic feet",
		decimalCount: 2,
		fromBase: (x)=>x/(0.3048**3),
		toBase: (x)=>x*(0.3048**3),
	},
	yard3: {
		abbreviation: "yd³",
		fullName: "cubic yard",
		decimalCount: 2,
		fromBase: (x)=>x/((0.3048*3)**3),
		toBase: (x)=>x*(0.3048*3)**3,
	},
	meter3: {
		abbreviation: "m³",
		fullName: "cubic meter",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},

	//---mass-weight---
	gram: {
		abbreviation: "g",
		decimalCount: 2,
		fromBase: (x)=>x/0.001,
		toBase: (x)=>x*0.001,
	},
	kilogram: {
		abbreviation: "kg",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	pounds: {
		abbreviation: "lb",
		decimalCount: 2,
		fromBase: (x)=>x/0.453592,
		toBase: (x)=>x*0.453592,
	},
	metricton: {
		abbreviation: "mt",
		fullName: "metric ton",
		decimalCount: 4,
		fromBase: (x)=>x/1000,
		toBase: (x)=>x*1000,
	},
	shortton: {
		abbreviation: "tn",
		fullName: "short ton",
		decimalCount: 4,
		fromBase: (x)=>x/907.185,
		toBase: (x)=>x*907.185,
	},

	//---density---
	poundsPerYard3: {
		abbreviation: "lb/yd³",
		decimalCount: 2,
		fullName: "pound per cubic yard",
		fromBase: (x)=>x/0.593276,
		toBase: (x)=>x*0.593276,
	},
	kilogramPerMeter3: {
		abbreviation: "kg/m³",
		decimalCount: 2,
		fullName: "kilogram per cubic meter",
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	gramPerCentimeter3: {
		abbreviation: "g/cc",
		decimalCount: 2,
		fullName: "gram per cubic centimeter",
		fromBase: (x)=>x/(0.001/(0.01**3)),
		toBase: (x)=>x*0.001/(0.01**3),
	},
	metrictonPerMeter3: {
		abbreviation: "mt/m³",
		decimalCount: 4,
		fullName: "metric ton per cubic meter",
		fromBase: (x)=>x/1000,
		toBase: (x)=>x*1000,
	},
	shorttonPerYard3: {
		abbreviation: "tn/yd³",
		decimalCount: 4,
		fullName: "short ton per cubic yard",
		fromBase: (x)=>x/(907.185/((0.3048*3)**3)),
		toBase: (x)=>x*907.185/((0.3048*3)**3),
	},
	metrictonPerMeter: {
		abbreviation: "mt/m",
		decimalCount: 2,
		fullName: "metric ton per meter",
		fromBase: (x) => x / 1000,
		toBase: (x) => x * 1000,
	},

	shorttonPerFeet: {
		abbreviation: "tn/ft",
		decimalCount: 2,
		fullName: "short ton per feet",
		fromBase: (x) => x / (907.185 / 0.3048),
		toBase: (x) => x * (907.185 / 0.3048),
	},

	meter3PerMeter: {
		abbreviation: "m³/m",
		decimalCount: 2,
		fullName: "cubic meter per meter",
		fromBase: (x) => x / 1,
		toBase: (x) => x * 1,
	},

	yard3PerFeet: {
		abbreviation: "yd³/ft",
		decimalCount: 2,
		fullName: "cubic yard per feet",
		fromBase: (x) => x / (((0.3048 * 3) ** 3) / 0.3048),
		toBase: (x) => x * (((0.3048 * 3) ** 3) / 0.3048),
	},

	//---pressure---
	pascal: {
		abbreviation: "Pa",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	kilopascal: {
		abbreviation: "kPa",
		decimalCount: 2,
		fromBase: (x)=>x/1000,
		toBase: (x)=>x*1000,
	},
	poundsPerInch2: {
		abbreviation: "psi",
		decimalCount: 2,
		fromBase: (x)=>x/6894.76,
		toBase: (x)=>x*6894.76,
	},
	bar: {
		abbreviation: "bar",
		decimalCount: 2,
		fromBase: (x)=>x/100000,
		toBase: (x)=>x*100000,
	},
	megapascal: {
		abbreviation: "MPa",
		decimalCount: 2,
		fromBase: (x)=>x/1000000,
		toBase: (x)=>x*1000000,
	},

	//---energy density---
	kilocaloriePerPound: {
		abbreviation: "kcal/lb",
		decimalCount: 2,
		fromBase: (x)=>x/0.00922414,
		toBase: (x)=>x*0.00922414,
	},
	megaJoulePerKilogram: {
		abbreviation: "MJ/kg",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},

	//---angle---
	degrees: {
		abbreviation: "°",
		decimalCount: 1,
		fullName: "degrees",
		fromBase: (x)=>x*180/math.PI,
		toBase: (x)=>x*math.PI/180,
	},
	radians: {
		abbreviation: "rad",
		decimalCount: 1,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	degrees360: {
		abbreviation: "°",
		decimalCount: 1,
		fullName: "degrees",
		fromBase: (x)=>{
			x=x*180/math.PI;
			x%=360;
			if(x<0)
				x+=360;
			return x;
		},
		toBase: (x)=>x*math.PI/180,
	},
	tanPercent:{
		abbreviation: "%",
		decimalCount: 2,
		fullName: "percent",
		fromBase: (x)=>math.tan(x)*100,
		toBase: (x)=>math.atan(x/100),
	},
	tanRatio:{
		abbreviation: "",
		decimalCount: 2,
		fullName: "",
		fromBase: (x)=>1/math.tan(x),
		toBase: (x)=>math.atan(1/x),
	},

	//---time---
	millisecond: {
		abbreviation: "ms",
		decimalCount: 0,
		fromBase: (x)=>x/0.001,
		toBase: (x)=>x*0.001,
	},
	second: {
		abbreviation: "s",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	minute: {
		abbreviation: "min",
		decimalCount: 2,
		fromBase: (x)=>x/60,
		toBase: (x)=>x*60,
	},
	hour: {
		abbreviation: "min",
		decimalCount: 2,
		fromBase: (x)=>x/3600,
		toBase: (x)=>x*3600,
	},
	percent: {
		abbreviation: "%",
		decimalCount: 2,
		fullName: 'percent',
		fromBase: (x)=>x/0.01,
		toBase: (x)=>x*0.01,
	},

	//---velocity
	millimeterPerSecond: {
		abbreviation: "mm/s",
		decimalCount: 3,
		fromBase: (x)=>x/0.001,
		toBase: (x)=>x*0.001,
	},
	inchPerSecond: {
		abbreviation: "in/s",
		decimalCount: 4,
		fromBase: (x)=>x/(0.3048/12),
		toBase: (x)=>x*0.3048/12,
	},
	feetPerSecond: {
		abbreviation: "ft/s",
		decimalCount: 2,
		fromBase: (x)=>x/0.3048,
		toBase: (x)=>x*0.3048,
	},
	meterPerSecond: {
		abbreviation: "m/s",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	kilometerPerHour: {
		abbreviation: "kmph",
		decimalCount: 2,
		fromBase: (x)=>x/(1000/3600),
		toBase: (x)=>x*1000/3600,
	},
	milePerHour: {
		abbreviation: "mph",
		decimalCount: 2,
		fromBase: (x)=>x/(1609.34/3600),
		toBase: (x)=>x*1609.34/3600,
	},
	meterPerMinute: {
		abbreviation: "m/min",
		decimalCount: 2,
		fromBase: (x)=>x/60,
		toBase: (x)=>x*60,
	},
	feetPerMinute: {
		abbreviation: "ft/min",
		decimalCount: 2,
		fromBase: (x)=>x/(0.3048/60),
		toBase: (x)=>x*0.3048/60,
	},

	//---time per distance---
	secondPerMeter: {
		abbreviation: "s/m",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	secondPerFeet: {
		abbreviation: "s/ft",
		decimalCount: 2,
		fromBase: (x)=>x/(1/0.3048),
		toBase: (x)=>x*1/0.3048,
	},
	millisecondPerMeter: {
		abbreviation: "ms/m",
		decimalCount: 2,
		fromBase: (x)=>x/(0.001),
		toBase: (x)=>x*0.001,
	},
	millisecondPerFeet: {
		abbreviation: "ms/ft",
		decimalCount: 2,
		fromBase: (x)=>x/(0.001/0.3048),
		toBase: (x)=>x*0.001/0.3048,
	},

	//---sound---
	deciBel: {
		abbreviation: "dB",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	megaDeciBel: {
		abbreviation: "MdB",
		decimalCount: 2,
		fromBase: (x)=>x/(1000000),
		toBase: (x)=>x*1000000,
	},

	//---weight per length---
	kilogramPerMeter: {
		abbreviation: "kg/m",
		decimalCount: 2,
		fullName: "kilograms per meter",
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	poundsPerFoot: {
		abbreviation: "lb/ft",
		decimalCount: 3,
		fullName: "pounds per foot",
		fromBase: (x)=>x*0.6719,
		toBase: (x)=>x*1.488
	},

	//---miscellaneous---
	meterPerSquareRootKg: {
		abbreviation: "m/√kg",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	meterPerCubeRootKg: {
		abbreviation: "m/∛kg",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	feetPerSquareRootPound: {
		abbreviation: "ft/√lb",
		decimalCount: 2,
		fromBase: (x)=>x/(0.3048/(0.453592**(1/2))),
		toBase: (x)=>x*0.3048/(0.453592**(1/2)),
	},
	feetPerCubeRootPound: {
		abbreviation: "ft/∛lb",
		decimalCount: 2,
		fromBase: (x)=>x/(0.3048/(0.453592**(1/3))),
		toBase: (x)=>x*0.3048/(0.453592**(1/3)),
	},

	//---temperature---
	kelvin: {
		abbreviation: "°",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	celsius: {
		abbreviation: "°",
		decimalCount: 2,
		fromBase: (x)=>x-273.15,
		toBase: (x)=>x+273.15,
	},
	fahrenheit: {
		abbreviation: "°",
		decimalCount: 2,
		fromBase: (x)=>(x - 273.15)*9/5 + 32,
		toBase: (x)=>(x-32)*5/9 +273.15,
	},

	//---frequency---
	hertz: {
		abbreviation: "Hz",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},

	// --rotation speed--
	rotationPerSecond: {
		abbreviation: "rps",
		fullName: "rotation per second",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},

	rotationPerMinute: {
		abbreviation: "rpm",
		fullName: "rotation per minute",
		decimalCount: 2,
		fromBase: (x)=>x*60,
		toBase: (x)=>x/60,
	},

	// ---torque---
	newtonMeter: {
		abbreviation: "Nm",
		fullName: "Newton meter",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	footPounds: {
		abbreviation: "lbf-ft",
		fullName: "foot-pounds",
		decimalCount: 2,
		fromBase: (x)=>x*0.737562147,
		toBase: (x)=>x/0.737562147,
	},

	// --force--
	newton: {
		abbreviation: "N",
		fullName: "Newton",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	poundForce: {
		abbreviation: "lbf",
		fullName: "pound-force",
		decimalCount: 2,
		fromBase: (x)=>x*0.2248089431,
		toBase: (x)=>x/0.2248089431,
	},

	//---misc---
	lonLatDegree: {
		abbreviation: "°",
		decimalCount: 9,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},

	//
	meterPerPixel: {
		abbreviation: "m/pixel",
		fullName: "meter/pixel",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	milliMeterPerPixel: {
		abbreviation: "mm/pixel",
		decimalCount: 2,
		fromBase: (x)=>x/(0.001),
		toBase: (x)=>x*0.001,
	},
	inchPerPixel: {
		abbreviation: "in/pixel",
		decimalCount: 4,
		fromBase: (x)=>x/(0.3048/12),
		toBase: (x)=>x*0.3048/12,
	},

	//---weight by large weight ratio---
	kilogramPerKilogram: {
		abbreviation: "kg/kg",
		decimalCount: 2,
		fromBase: (x)=>x,
		toBase: (x)=>x,
	},
	kilogramPerMetricton: {
		abbreviation: "kg/mt",
		decimalCount: 2,
		fullName: "kilogram per metric ton",
		fromBase: (x)=>x/(1/1000),
		toBase: (x)=>x*1/1000,
	},
	poundsPerShortton: {
		abbreviation: "lb/tn",
		decimalCount: 2,
		fullName: "pounds per short ton",
		fromBase: (x)=>x/(1/2000),
		toBase: (x)=>x*1/2000,
	},
	yard3PerPound: {
		abbreviation: "yd³/lb",
		decimalCount: 2,
		fullName: "cubic yard per pound",
		fromBase: (x)=>1/(x/0.593276),
		toBase: (x)=>(1/x)*0.593276,
	},
	meter3PerKilogram: {
		abbreviation: "m³/kg",
		decimalCount: 2,
		fullName: "cubic meter per kilogram",
		fromBase: (x)=>1/x,
		toBase: (x)=>1/x,
	},
	metricTonPerKilogram: {
		abbreviation: "mt/kg",
		decimalCount: 2,
		fullName: "metric ton per kilogram",
		fromBase: (x)=>1/(1000*x),
		toBase: (x)=>1/(1000*x),
	},
	shortTonPerPound: {
		abbreviation: "tn/lb",
		decimalCount: 2,
		fullName: "short ton per pound",
		fromBase: (x)=>1/(2000*x),
		toBase: (x)=>1/(2000*x),
	}
});

export type Unit=keyof typeof unitMeta;
